/* eslint-disable no-console */
/* eslint-disable dot-notation */
import { EventLogData } from '@moderepo/mode-apis';
import { capitalizeFirst, MultiMetricsDataPoint } from '@moderepo/mode-app-components';
import { Entity } from '@moderepo/mode-internal-apis';
import * as am4charts from '@amcharts/amcharts4/charts';
import { EntityUIComponentAdapterCollection } from '.';
import { EntityCombinedMetricsViewUIComponentAdapter, EntityCombinedMetricsViewUIComponentAdapterDefaultParams } from '..';
import { ChartMetricInfo, ExportDataFormat, FetchEventLogDataParams, groupEventLogRecordsByGroupId, GanttChartDataPoint }
    from '../../..';
import { getNumericMetricCustomSettings } from '../..';
import { ReadonlyNonEmptyArray } from '../../../../utils/utils';



/**
 * For Maeda, we need to sort tag metrics based on it' location so we need to extends the ChartMetricInfo and use this interface instead.
 */
interface MaedaChartMetricInfo extends ChartMetricInfo {
    readonly block: string;
    readonly holeNumber: string;
    readonly stage: number;
    readonly locationX: number;
    readonly locationY: number;
    readonly locationZ: number;
}



/**
 * Create tag metrics info based on the provided event log. This function is Maeda specific because it have some logic that is only used for
 * maeda such as using location data from the event log
 */
const convertEventLogRecordsToTagMetrics = (params: {
    eventLogData: EventLogData,
    eventLogInfo: FetchEventLogDataParams,
}): readonly MaedaChartMetricInfo[] => {

    // First, call the groupEventLogRecordsByGroupId to group the records
    const eventRecordsGroupedByGroupId = groupEventLogRecordsByGroupId(params.eventLogData, params.eventLogInfo);

    /**
     * Create tag metrics based on the number of event groups. 1 tag metric per group.
     * We will create objects of type MaedaChartMetricInfo so that we can add other attributes to the metrics e.g. location attributes
     */
    const tagMetrics = Object.entries(eventRecordsGroupedByGroupId).filter(([, records]) => {
        return records.length > 0;
    }).map(([groupId, records]): MaedaChartMetricInfo => {
        return {
            id         : groupId,
            name       : groupId,
            displayName: groupId,
            block      : records[0].fields['block'],
            holeNumber : records[0].fields['holeNumber'],
            stage      : records[0].fields['stage'],
            locationX  : records[0].fields['coordinate_x'],     // all records in the same group should have the same location values
            locationY  : records[0].fields['coordinate_y'],     // so we just need to get the location values from the first record.
            locationZ  : records[0].fields['coordinate_z'],
        };
    });

    return tagMetrics;
};



interface Point {
    readonly x: number;
    readonly y: number;
}


/**
 * Calculate the distance between 2 points on a 2D plane following this formula
 *      d^2 = x^2 + y^2
 *      d = sqrt(x^2 + y^2)
 *
 *     p1
 *      |\
 *      | \
 *    y |  \ d
 *      |   \
 *      |    \
 *       -----  p2
 *         x
 */
const getDistance = (point1: Point, point2: Point): number => {
    const x = Math.abs(point2.x - point1.x);
    const y = Math.abs(point2.y - point1.y);
    return Math.sqrt(x * x + y * y);
};



/**
 * Get the (X, Y) coordinate of the selected numeric metric (water pressure sensor). Use (0, 0) if numericMetric is not selected
 */
const getSelectedSensorLocation = (selectedNumericMetric: ChartMetricInfo | undefined, entity: Entity) => {
    if (selectedNumericMetric) {
        // Get the metric's coordinate from the entity's attribute
        const coord = entity.attributes?.coordinates?.[selectedNumericMetric.name];
        if (coord) {
            return {
                x : coord['x'],
                y : coord['y'],
                el: coord['el'],
            };
        }
    }

    return {
        x: 0, y: 0, el: 0,
    };
};


/**
 * Sort the given tag metrics by the distance between the tag metrics and the selected selectedNumericMetric/water sensor.
 *
 * This is maeda specific logic for sorting tag metrics.
 */
const sortTagMetrics = (params: EntityCombinedMetricsViewUIComponentAdapterDefaultParams): readonly ChartMetricInfo[] => {

    // Make a copy of the tagMetrics since it is readonly array
    const newTagMetrics = [...(params.tagMetrics ?? [])];

    // Get the (X, Y) coordinate of the selected numeric metric (water pressure sensor)
    const selectedSensorLocation = getSelectedSensorLocation(params.selectedNumericMetric, params.entity);
    const selectedDate = params.selectedDate;

    // First, find all the tagMetrics that have some data points that crossed the selected date. Mark these metrics as true if they do.
    const tagMetricsThatCrossedSelectedDate: {
        [metricId: string]: boolean | undefined;
    } = {};

    if (selectedDate !== undefined) {
        newTagMetrics.forEach((tagMetric) => {
            const ganttChartDataPoints = params.ganttChartDataPoints?.[tagMetric.id];

            // Go though the data points for this tag metric and find 1 data point that the selectedDate is between the data point's start/end date
            // If we can find 1 data points that crossed to the selected date then that mean this tagMetric is in selection.
            const isMetricInSelection = ganttChartDataPoints?.some((dataPoint) => {
                return dataPoint.startDate <= selectedDate && selectedDate <= dataPoint.endDate;
            });

            // Add the flag to the tagMetricsThatCrossedSelectedDate
            tagMetricsThatCrossedSelectedDate[tagMetric.id] = isMetricInSelection;
        });
    }


    // Sort the newTagMetrics by the distance between the tag metric and the selected numeric metric (water sensor)
    newTagMetrics.sort((tagMetric1, tagMetric2) => {

        // Compare the distance between tagMetric1 and selectedMetricLocation (D1) and the distance between tagMetric2 and selectedMetricLocation (D2)
        // When we create the tag metrics convertEventLogRecordsToTagChartData, we created instances of MaedaChartMetricInfo therefore we can
        // cast them back to MaedaChartMetricInfo and use the locationX and locationY attributes.
        const metric1 = tagMetric1 as MaedaChartMetricInfo;
        const metric2 = tagMetric2 as MaedaChartMetricInfo;

        if (tagMetric1 && tagMetric2) {

            const distance1 = getDistance(selectedSensorLocation, { x: metric1.locationX ?? 0, y: metric1.locationY ?? 0 });
            const distance2 = getDistance(selectedSensorLocation, { x: metric2.locationX ?? 0, y: metric2.locationY ?? 0 });

            const isMetric1CrossedSelectedDate = tagMetricsThatCrossedSelectedDate[tagMetric1.id];
            const isMetric2CrossedSelectedDate = tagMetricsThatCrossedSelectedDate[tagMetric2.id];

            if (isMetric1CrossedSelectedDate && isMetric2CrossedSelectedDate) {
                // If both metrics crossed the selected date then we compare their distance to the water sensor
                return distance1 - distance2;
            } if (isMetric1CrossedSelectedDate) {
                // If only the metric 1 crossed the selected date then metric 1 should show first
                return -1;
            } if (isMetric2CrossedSelectedDate) {
                // If only the metric 2 crossed the selected date then metric 2 should show first
                return 1;
            }

            // If neither metrics crossed the selected date then also compare them by distance so that all the metrics that do not
            // cross the selected date are also sorted by distance.
            return distance1 - distance2;
        }

        return 0;
    });


    return newTagMetrics;
};



/**
 * Implement the adapter's getNumericMetricChartYAxisTextLabel to customize the labels
 */
const getNumericMetricChartYAxisTextLabel = (params: {
    readonly text: string | undefined;
    readonly targetAxis: am4charts.AxisLabel;
    readonly targetMetric: ChartMetricInfo;
} & EntityCombinedMetricsViewUIComponentAdapterDefaultParams): string | undefined => {

    if (params.targetAxis.dataItem.values.value.value !== undefined) {

        // Get the value from the axis and round it to 2 decimal places
        const formattedValue = params.targetAxis.dataItem.values.value.value.toFixed(2);

        if (params.targetMetric.unit) {
            return `${formattedValue} ${params.targetMetric.unit}`;
        }
        return `${formattedValue}`;
    }

    return params.text;
};



/**
 * Implement the adapter's getTagMetricChartYAxisHTMLLabel to customize the labels
 */
const getTagMetricChartYAxisHTMLLabel = (params: {
    readonly text: string | undefined;
    readonly targetAxis: am4charts.AxisLabel;
    readonly targetMetric: ChartMetricInfo;
} & EntityCombinedMetricsViewUIComponentAdapterDefaultParams): string | undefined => {
    // Get the data point that triggered this function. If there is a dataPoint, return the dataPoint.displayName. Else return whatever 'text' is
    const dataPoint = params.targetAxis.dataItem ? params.targetAxis.dataItem.dataContext as unknown as GanttChartDataPoint : undefined;


    if (dataPoint && dataPoint.displayName) {

        // Get the (X, Y) coordinate of the selected numeric metric (water pressure sensor)
        const selectedSensorLocation = getSelectedSensorLocation(params.selectedNumericMetric, params.entity);

        // Get the distance from the tagMetric (grout progress) to the selectedSensorLocation
        const distance = Math.round(getDistance(selectedSensorLocation, {
            x: (params.targetMetric as MaedaChartMetricInfo).locationX,
            y: (params.targetMetric as MaedaChartMetricInfo).locationY,
        }));

        const borderStyle = `1px solid ${params.theme.palette.text.primary}`;

        // We know the displayName is a combination of the event log field values separated by : so we can split it by : to get the field values
        const fieldValues = dataPoint.displayName.split(':');

        // Add distance value to the list of fieldValues so that it will be included in the label
        fieldValues.push(`${distance.toString()} m`);

        // Create an HTML element for each field value
        const fieldsHTML = fieldValues.map((field, index) => {
            const fieldElementStyle = `
                min-width:${index < fieldValues.length - 1 ? 3 : 4}em; display:inline-block; text-align: center;
                border-right: ${index < fieldValues.length - 1 ? borderStyle : 'none'};
            `;

            return (`
                <span style="${fieldElementStyle}">
                    ${field}
                </span>    
            `);
        });

        return `
            <div style="height:100%; border: ${borderStyle};">
                ${fieldsHTML.join('')}
            </div>
        `;
    }
    return params.text;
};


/**
 * Given an array of data points, a starting index, and a metricId, find a data point for which the value for the specified metricId
 * in that data point is not null or undefined. Start from startIndex and go backward until we find a non null value or end of the array.
 */
const getPreviousNonNullDataPoint = (
    dataPoints: readonly MultiMetricsDataPoint[] | undefined, startIndex: number, metricId: string,
): MultiMetricsDataPoint | undefined => {
    if (dataPoints && startIndex >= 0) {
        for (let i = startIndex; i >= 0; i -= 1) {
            const metricValue = dataPoints[i][metricId];
            if (metricValue !== null && metricValue !== undefined) {
                return dataPoints[i];
            }
        }
    }
    return undefined;
};



/**
 * The amcharts adapter function to return a tooltip text for the numeric charts. For Maeda, they not only want to show the value of the
 * data point that the mouse is hovering over but they also want to show the previous value AND the difference between the 2 values.
 */
const getNumericMetricChartTooltipText = (params: {
    readonly text: string | undefined;
    readonly targetSeries: am4charts.XYSeries;
    readonly targetMetric: ChartMetricInfo;
} & EntityCombinedMetricsViewUIComponentAdapterDefaultParams): string | undefined => {

    // Get the ID of the metric which the mouse is hovering over
    const metricId = params.targetMetric.id;

    // Get the index and the dataPoint that the mouse is hovering over
    const currentValueIndex = params.targetSeries.tooltipDataItem.index;
    const currentDataPoint = params.metricsDataPoints?.[currentValueIndex];

    if (currentDataPoint) {

        // Get the decimalPlaces setting from the metric's customSettings if there is one. Use 3 as default if there isn't one.
        const metricCustomSettings = getNumericMetricCustomSettings(params.targetMetric.name, undefined, params.customSettings);
        const decimalPlaces = metricCustomSettings?.decimalPlaces ?? 3;
        const unit = params.targetMetric.unit ? ` ${params.targetMetric.unit}` : '';

        const currentValue = currentDataPoint[metricId];
        const formattedCurrentValue = typeof currentValue === 'number' ? `${currentValue.toFixed(decimalPlaces)}${unit}` : '';

        // Now find the previous data point. Because dataPoint can have null value, we can't just use the dataPoint at currentIndex - 1,
        // we need to loop through all the dataPoints starting from currentIndex - 1 until we find a non null data point.
        const previousDataPoint = getPreviousNonNullDataPoint(params.metricsDataPoints, currentValueIndex - 1, metricId);
        const previousValue = previousDataPoint?.[metricId];
        const formattedPreviousValue = typeof previousValue === 'number' ? `${previousValue.toFixed(decimalPlaces)}${unit}` : '';

        // Get the different between the currentValue and previousValue and then format it to e.g. "+2.543 m" or "-1.233 m"
        const diffValue = typeof currentValue === 'number' && typeof previousValue === 'number' ? currentValue - previousValue : 0;
        const roundedDiffValue = `${diffValue > 0 ? '+' : ''}${diffValue.toFixed(decimalPlaces)}${unit}`;

        // Create and return the tooltip
        const tooltipText = `[bold]${params.targetMetric.displayName}[/]
             ${capitalizeFirst(params.trans('label.current_value'))}: ${formattedCurrentValue} (${roundedDiffValue})
             ${capitalizeFirst(params.trans('label.previous_value'))}: ${formattedPreviousValue}
        `;
        return tooltipText;
    }

    return params.text;
};



/**
 * Return a list of export file format that this handler supports
 */
const getSupportedExportDataFormats = (): ReadonlyNonEmptyArray<ExportDataFormat> => {
    return [{
        format        : '3DSystem_JSON',
        fileExtension : 'json',
        displayNameKey: '3DSystem JSON Format',
    }];
};


/**
 * Create the content for the export file for the given data
 */
const createExportFileContent = (params: {
    readonly selectedDataFormat: string;
    readonly selectedNumericMetrics: readonly ChartMetricInfo[];
    readonly selectedTagMetrics: readonly ChartMetricInfo[];
} & EntityCombinedMetricsViewUIComponentAdapterDefaultParams): string | undefined => {
    const result = {};

    params.selectedNumericMetrics.forEach((metric) => {
        const sensorLocation = getSelectedSensorLocation(metric, params.entity);
        result[metric.name] = {
            name: metric.displayName,
            ...sensorLocation,
        };
    });

    params.selectedTagMetrics.forEach((metric) => {
        const tagMetric = metric as MaedaChartMetricInfo;
        result[metric.name] = {
            name  : metric.displayName,
            block : tagMetric.block,
            holeNo: tagMetric.holeNumber,
            stage : tagMetric.stage,
            x     : tagMetric.locationX,
            y     : tagMetric.locationY,
            el    : tagMetric.locationZ,
        };
    });

    return JSON.stringify(result, undefined, 4);
};


/**
 * Create an object that implements the EntityCombinedMetricsViewUIComponentAdapter interface. For maeda, we need to implement the
 * convertEventLogRecordsToTagMetrics and sortTagMetrics functions.
 */
const maedaEntityCombinedMetricsViewUIComponentAdapter: EntityCombinedMetricsViewUIComponentAdapter = {
    convertEventLogRecordsToTagMetrics,
    sortTagMetrics,
    getNumericMetricChartYAxisTextLabel,
    getTagMetricChartYAxisHTMLLabel,
    getNumericMetricChartTooltipText,
    getSupportedExportDataFormats,
    createExportFileContent,
};


// Register the adapter with the EntityUIComponentAdapterCollection
EntityUIComponentAdapterCollection.getInstance().registerAdapter(
    'MaedaEntityCombinedMetricsViewUIComponentAdapter', maedaEntityCombinedMetricsViewUIComponentAdapter,
);
